import React, { FormEvent, useEffect, useRef, useState } from "react";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { PostMedia, ViewerMessage } from "src/components/CheckedIn/CheckedInContentStyles";
import { CheckedInIcon } from "src/components/CheckedIn/CheckedInIcon";
import { CheckedInUser } from "src/components/CheckedIn/checkedInUser";
import { AppSurvey, submitSurveyAnswers, SurveyQuestion } from "./surveyApi";
import { invalidQuestions, orderedQuestions, questionType, serializeAnswers, SURVEY_CHOICES } from "./surveyAnswers";
import { SurveyQuestionInput } from "./SurveyQuestionInput";
import { CompanyLogo, SurveyError, SurveyForm, SurveyIntro, SurveySubmit } from "./SurveyViewerStyles";

interface SurveyResponseProps {
    survey: AppSurvey;
    user: CheckedInUser;
    onClose: () => void;
    onSubmitted: () => void;
}

export function surveyLogo(survey: AppSurvey, user: CheckedInUser): string | null {
    return user.companyLogoUrl || (survey.image.isUsingCompanyLogo ? survey.image.imageUrl : null);
}

export default function SurveyResponse({ survey, user, onClose, onSubmitted }: SurveyResponseProps): React.ReactElement {
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [invalidIds, setInvalidIds] = useState<string[]>([]);
    const [error, setError] = useState<string>("");
    const [busy, setBusy] = useState<boolean>(false);
    const [failedImage, setFailedImage] = useState<boolean>(false);
    const [failedLogo, setFailedLogo] = useState<boolean>(false);
    const submitting = useRef<boolean>(false);
    const mounted = useRef<boolean>(true);
    const questions: SurveyQuestion[] = orderedQuestions(survey);
    const unsupported: boolean = questions.some(question =>
        !Object.prototype.hasOwnProperty.call(SURVEY_CHOICES, questionType(survey, question)));
    const logo: string | null = surveyLogo(survey, user);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const submit = async (event: FormEvent): Promise<void> => {
        event.preventDefault();
        if (submitting.current || questions.length === 0 || unsupported) {
            return;
        }
        const missing: SurveyQuestion[] = invalidQuestions(survey, answers);
        setInvalidIds(missing.map(question => question.uuid));
        if (missing.length > 0) {
            document.getElementById(`survey-question-${missing[0].uuid}`)?.focus();
            return;
        }
        submitting.current = true;
        setBusy(true);
        setError("");
        try {
            await submitSurveyAnswers(survey.uuid, serializeAnswers(survey, answers));
            if (mounted.current) {
                onSubmitted();
            }
        } catch {
            if (mounted.current) {
                setError("We couldn’t submit your survey. Your answers are still here. Please try again.");
            }
        } finally {
            submitting.current = false;
            if (mounted.current) {
                setBusy(false);
            }
        }
    };

    return <>
        <SurveyIntro>
            <CheckedInAction type="button" $round aria-label="Close survey" onClick={onClose} disabled={busy}>
                <CheckedInIcon name="close" /></CheckedInAction>
            {logo && !failedLogo && <CompanyLogo src={logo} alt={user.companyName || "Company logo"}
                onError={() => setFailedLogo(true)} />}
            <p>Your opinion matters to us! We would appreciate your feedback on the following:</p>
            {survey.isAnonymous && <p>Your responses are anonymous.</p>}
        </SurveyIntro>
        {!survey.image.isUsingCompanyLogo && survey.image.imageUrl && !failedImage && <PostMedia $detail>
            <img src={survey.image.imageUrl} alt="" onError={() => setFailedImage(true)} />
        </PostMedia>}
        <SurveyForm onSubmit={submit} noValidate aria-label={survey.name}>
            <h2>{survey.name}</h2>
            {questions.map(question => <SurveyQuestionInput key={question.uuid} survey={survey} question={question}
                answer={answers[question.uuid] ?? ""} disabled={busy} error={invalidIds.includes(question.uuid)}
                onChange={value => {
                    setAnswers(current => ({ ...current, [question.uuid]: value }));
                    setInvalidIds(current => current.filter(uuid => uuid !== question.uuid));
                }} />)}
            {(unsupported || questions.length === 0) && <ViewerMessage role="status">
                This survey is currently unavailable. Please check back later.
            </ViewerMessage>}
            <SurveySubmit>
                {error && <SurveyError role="alert">{error}</SurveyError>}
                <CheckedInAction type="submit" $monochrome disabled={busy || unsupported || questions.length === 0}>
                    {busy ? "Submitting…" : "Submit"}
                </CheckedInAction>
            </SurveySubmit>
        </SurveyForm>
    </>;
}
